// src/components/PdfAnnotator.tsx

import React, {
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
    useCallback,
  } from 'react';
  import { Document, Page, pdfjs } from 'react-pdf';
  import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
  import 'react-pdf/dist/esm/Page/TextLayer.css';
import { DiscoveredPdfField, PdfRect } from '../../../../interfaces/dynamicForms/MappingTypes';
  
  pdfjs.GlobalWorkerOptions.workerSrc =
    `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
  
  interface PdfAnnotatorProps {
    // onFileChange?: (file: File | null) => void; 
    initialFile?: File | null; 
    onFieldsExtracted: (fields: DiscoveredPdfField[]) => void;
    onNewFileSelect?: (file: File) => void; 
  }
  
  const PdfAnnotator: React.FC<PdfAnnotatorProps> = ({
    // onFileChange,
    onFieldsExtracted,
    initialFile,
    onNewFileSelect
  }) => {
    const [pdfFile, setPdfFile] = useState<File | null>(initialFile || null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [fieldData, setFieldData] = useState<DiscoveredPdfField[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
  
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
  
    const [originalPageDimensions, setOriginalPageDimensions] = useState<{ width: number; height: number } | null>(null);
    const [zoomLevel, setZoomLevel] = useState<number>(1);
  
    useEffect(() => {
        if (initialFile) {
            setPdfFile(initialFile);
            processPdfFields(initialFile);
        }
    }, [initialFile]);

    useEffect(() => {
      const measure = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };
      measure();
      const ro = new ResizeObserver(measure);
      if (containerRef.current) ro.observe(containerRef.current);
      return () => ro.disconnect();
    }, []);
  
    useLayoutEffect(() => {
      if (originalPageDimensions && containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    }, [originalPageDimensions]);
  
    const processPdfFields = async (file: File) => {
      setIsLoading(true);
      setError(null);
      try {
        const buffer = await file.arrayBuffer();
        const pdfDoc = await pdfjs.getDocument({ data: buffer }).promise;
        const extracted: DiscoveredPdfField[] = [];
  
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          const page = await pdfDoc.getPage(i);
          const annots = await page.getAnnotations();
          const viewport = page.getViewport({ scale: 1 });
  
          annots.forEach((a: any) => {
            if (a.subtype === 'Widget' && a.fieldName) {
              const [x0, y0, x1, y1] = a.rect as PdfRect;
              const cssY0 = viewport.height - y1;
              const cssY1 = viewport.height - y0;
  
              let type: DiscoveredPdfField['type'] = 'unknown';
              if (a.fieldType === 'Tx') type = 'text';
              else if (a.fieldType === 'Btn') type = a.buttonValue ? 'radio' : 'checkbox';
              else if (a.fieldType === 'Ch') type = 'dropdown';
              else if (a.fieldType === 'Sig') type = 'signature';
  
              extracted.push({
                name: a.fieldName,
                rect: [x0, cssY0, x1, cssY1],
                pageNumber: i,
                type,
              });
            }
          });
        }
  
        setFieldData(extracted);
        onFieldsExtracted(extracted);
      } catch (err: any) {
        console.error(err);
        setError(`Failed to process PDF: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;

      //   if (onFileChange) {
    //     onFileChange(file);
    //   }
      if (file) {
        setPdfFile(file);
        setFieldData([]);
        setNumPages(null);
        setCurrentPage(1);
        setError(null);
        setOriginalPageDimensions(null);
        processPdfFields(file);
        if (onNewFileSelect) {
            onNewFileSelect(file);
        }
      } else {
        setPdfFile(null);
        setFieldData([]);
        onFieldsExtracted([]);
      }
    };
  
    const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setCurrentPage(1);
    }, []);
  
    const onPageRenderSuccess = useCallback((page: any) => {
      const viewport = page.getViewport({ scale: 1 });
      setOriginalPageDimensions((prev) => {
        if (prev && prev.width === viewport.width && prev.height === viewport.height) {
          return prev;
        }
        return { width: viewport.width, height: viewport.height };
      });
    }, []);
  
    // Navigation
    const goPrev = () => {
      setOriginalPageDimensions(null);
      setCurrentPage((p) => Math.max(p - 1, 1));
    };
    const goNext = () => {
      setOriginalPageDimensions(null);
      setCurrentPage((p) => Math.min(p + 1, numPages ?? 1));
    };
  
    const currentFields = fieldData.filter(
      (f) => f.pageNumber === currentPage
    );
  
    // Base scale and then apply zoom
    const baseScale =
      originalPageDimensions && containerWidth > 0
        ? containerWidth / originalPageDimensions.width
        : 0;
    const scaleFactor = baseScale * zoomLevel;
  
    // Handle ctrl + wheel for zoom
    const handleWheel = (e: React.WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        setZoomLevel((z) =>
          e.deltaY < 0
            ? Math.min(z + 0.25, 3)
            : Math.max(z - 0.25, 0.25)
        );
      }
    };
  
    return (
      <div style={{ padding: 10 }}>
        <h2>PDF Viewer</h2>
        <input
          type="file"
          accept="application/pdf"
          onChange={handleFileChange}
        />
  
        {isLoading && <p>Processing PDF…</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}
  
        {pdfFile && !isLoading && !error && (
          <div style={{ marginTop: 20, position: 'relative' }}>
            {/* Zoom Controls */}
            <div
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 20,
                display: 'flex',
                gap: '4px',
              }}
            >
              <button onClick={() => setZoomLevel((z) => Math.min(z + 0.25, 3))}>
                +
              </button>
              <button onClick={() => setZoomLevel((z) => Math.max(z - 0.25, 0.25))}>
                –
              </button>
            </div>
  
            <div
              ref={containerRef}
              onWheel={handleWheel}
              style={{
                position: 'relative',
                margin: 'auto',
                overflow: 'auto',
                maxWidth: '100%',
                minHeight: 200,
                border: '1px solid #ccc',
              }}
            >
              <Document
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
                loading="Loading PDF…"
                error="Failed to load PDF."
                noData="No file selected."
              >
                <Page
                  key={currentPage}
                  pageNumber={currentPage}
                  width={containerWidth * zoomLevel}
                  renderAnnotationLayer={false}
                  renderTextLayer={true}
                  onRenderSuccess={onPageRenderSuccess}
                />
              </Document>
  
              {scaleFactor > 0 &&
                currentFields.map((f, i) => {
                  const [x0, y0, x1, y1] = f.rect;
                  const left = x0 * scaleFactor;
                  const top = y0 * scaleFactor;
                  const w = (x1 - x0) * scaleFactor;
                  const h = (y1 - y0) * scaleFactor;
                  const fontSize = 8 * zoomLevel;  // linear scaling of text
  
                  return (
                    <React.Fragment key={i}>
                      <div
                        style={{
                          position: 'absolute',
                          left,
                          top,
                          width: w,
                          height: h,
                          border: '2px solid red',
                          pointerEvents: 'none',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          left,
                          top: top + h + 4,
                          fontSize: `${fontSize}px`,
                          background: 'rgba(255,255,255,0.8)',
                          padding: '1px 3px',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                        }}
                      >
                        {f.name}
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
  
            {/* Navigation Buttons */}
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <button onClick={goPrev} disabled={currentPage <= 1}>
                ← Prev
              </button>
              <span style={{ margin: '0 12px' }}>
                Page {currentPage} / {numPages}
              </span>
              <button onClick={goNext} disabled={currentPage >= (numPages ?? 1)}>
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  export default PdfAnnotator;
  